import { useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Gift, History, Loader2, MinusCircle, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '~/api/trpc';

type BundleVouchersDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    customerId: number;
    isAdmin?: boolean;
};

const BundleVouchersDialog = ({
    open,
    onOpenChange,
    customerId,
    isAdmin = false,
}: BundleVouchersDialogProps) => {
    const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(null);
    const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
    const [adjustmentUnits, setAdjustmentUnits] = useState<number>(0);
    const [adjustmentReason, setAdjustmentReason] = useState('');
    const trpcUtils = trpc.useUtils();

    const { data: vouchers, isLoading } = trpc.customerMoney.getCustomerBundleVouchers.useQuery(
        { customerId },
        { enabled: open }
    );

    const { data: selectedVoucherTransactions } = trpc.customerMoney.getBundleVoucherTransactions.useQuery(
        { bundleVoucherId: selectedVoucherId! },
        { enabled: !!selectedVoucherId }
    );

    const adjustMutation = trpc.customerMoney.adjustBundleVoucher.useMutation({
        onSuccess: () => {
            toast.success('Gutschein wurde angepasst');
            trpcUtils.customerMoney.getCustomerBundleVouchers.invalidate({ customerId });
            trpcUtils.customerMoney.getBundleVoucherTransactions.invalidate({ bundleVoucherId: selectedVoucherId! });
            setShowAdjustmentForm(false);
            setAdjustmentUnits(0);
            setAdjustmentReason('');
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(amount);
    };

    const handleAdjust = () => {
        if (!selectedVoucherId || adjustmentUnits === 0 || !adjustmentReason.trim()) {
            return;
        }
        adjustMutation.mutate({
            bundleVoucherId: selectedVoucherId,
            quantityUnits: adjustmentUnits,
            reason: adjustmentReason.trim(),
        });
    };

    const activeVouchers = vouchers?.filter(v => v.remainingUnits > 0) ?? [];
    const expiredVouchers = vouchers?.filter(v => v.remainingUnits <= 0) ?? [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-4xl max-h-[80vh] overflow-hidden flex flex-col'>
                <DialogHeader>
                    <DialogTitle>Bundle-Gutscheine</DialogTitle>
                    <DialogDescription>
                        Übersicht aller Bundle-Gutscheine des Kunden
                    </DialogDescription>
                </DialogHeader>

                <div className='flex-1 overflow-auto'>
                    {isLoading ? (
                        <div className='space-y-2'>
                            {[...Array(5)].map((_, i) => (
                                <Skeleton key={i} className='h-12 w-full' />
                            ))}
                        </div>
                    ) : vouchers?.length === 0 ? (
                        <div className='text-center py-8 text-muted-foreground'>
                            Keine Bundle-Gutscheine vorhanden
                        </div>
                    ) : (
                        <div className='space-y-4'>
                        {/* Active Vouchers */}
                        {activeVouchers.length > 0 && (
                            <div className='space-y-2'>
                                <h4 className='font-semibold text-sm text-muted-foreground'>
                                    Aktive Gutscheine ({activeVouchers.length})
                                </h4>
                                {activeVouchers.map((voucher) => {
                                    const usedPercentage = ((voucher.unitQuantity - voucher.remainingUnits) / voucher.unitQuantity) * 100;
                                    const isSelected = selectedVoucherId === voucher.id;

                                    return (
                                        <Card
                                            key={voucher.id}
                                            className={`cursor-pointer transition-colors ${
                                                isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                                            }`}
                                            onClick={() => {
                                                setSelectedVoucherId(isSelected ? null : voucher.id);
                                                setShowAdjustmentForm(false);
                                            }}
                                        >
                                            <CardHeader className='pb-2'>
                                                <div className='flex justify-between items-start'>
                                                    <div>
                                                        <CardTitle className='text-base'>
                                                            {voucher.treatmentVariantName}
                                                        </CardTitle>
                                                        <CardDescription>
                                                            {formatCurrency(voucher.unitPriceGross)} pro Einheit
                                                        </CardDescription>
                                                    </div>
                                                    <Badge variant='secondary' className='bg-green-100 text-green-800'>
                                                        {voucher.remainingUnits} / {voucher.unitQuantity} Einheiten
                                                    </Badge>
                                                </div>
                                            </CardHeader>
                                            <CardContent className='space-y-2'>
                                                <Progress value={100 - usedPercentage} className='h-2' />
                                                <div className='flex justify-between text-xs text-muted-foreground'>
                                                    <span>
                                                        Restwert: {formatCurrency(voucher.remainingValueEur)}
                                                    </span>
                                                    <span>
                                                        Aus: {voucher.sourceBillingDraftNumber}
                                                    </span>
                                                </div>

                                                {/* Expanded section when selected */}
                                                {isSelected && (
                                                    <div className='mt-4 pt-4 border-t space-y-3' onClick={e => e.stopPropagation()}>
                                                        <div className='flex items-center gap-2'>
                                                            <History className='h-4 w-4' />
                                                            <span className='text-sm font-medium'>
                                                                Transaktionen ({voucher.transactionCount})
                                                            </span>
                                                        </div>

                                                        {selectedVoucherTransactions && selectedVoucherTransactions.length > 0 ? (
                                                            <div className='space-y-1 max-h-40 overflow-y-auto'>
                                                                {selectedVoucherTransactions.map((tx) => (
                                                                    <div
                                                                        key={tx.id}
                                                                        className='flex justify-between items-center text-xs py-1 px-2 bg-muted rounded'
                                                                    >
                                                                        <div className='flex items-center gap-2 flex-wrap'>
                                                                            <span className={tx.quantityUnits > 0 ? 'text-green-600' : 'text-red-600'}>
                                                                                {tx.quantityUnits > 0 ? '+' : ''}{tx.quantityUnits} Einheiten
                                                                            </span>
                                                                            <span className='text-muted-foreground'>
                                                                                {tx.type === 'REDEMPTION' && 'Eingelöst'}
                                                                                {tx.type === 'REFUND' && 'Erstattet'}
                                                                                {tx.type === 'ADJUSTMENT' && 'Korrektur'}
                                                                            </span>
                                                                            {tx.billingDraftLineItem?.invoice && (
                                                                                <span className='font-mono text-muted-foreground'>
                                                                                    Rg. {tx.billingDraftLineItem.invoice.invoiceNumber}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <span className='text-muted-foreground shrink-0 ml-2'>
                                                                            {format(new Date(tx.createdAt), 'dd.MM.yyyy', { locale: de })}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className='text-xs text-muted-foreground'>
                                                                Noch keine Transaktionen
                                                            </p>
                                                        )}

                                                        {/* Admin adjustment section */}
                                                        {isAdmin && (
                                                            <>
                                                                <Separator />
                                                                {!showAdjustmentForm ? (
                                                                    <Button
                                                                        variant='outline'
                                                                        size='sm'
                                                                        onClick={() => setShowAdjustmentForm(true)}
                                                                    >
                                                                        Manuell anpassen
                                                                    </Button>
                                                                ) : (
                                                                    <div className='space-y-3 p-3 border rounded-lg bg-muted/50'>
                                                                        <div className='space-y-2'>
                                                                            <Label>Einheiten hinzufügen/entfernen</Label>
                                                                            <div className='flex items-center gap-2'>
                                                                                <Button
                                                                                    type='button'
                                                                                    variant='outline'
                                                                                    size='icon'
                                                                                    onClick={() => setAdjustmentUnits(adjustmentUnits - 1)}
                                                                                >
                                                                                    <MinusCircle className='h-4 w-4' />
                                                                                </Button>
                                                                                <Input
                                                                                    type='number'
                                                                                    value={adjustmentUnits}
                                                                                    onChange={(e) => setAdjustmentUnits(parseInt(e.target.value) || 0)}
                                                                                    className='w-20 text-center'
                                                                                />
                                                                                <Button
                                                                                    type='button'
                                                                                    variant='outline'
                                                                                    size='icon'
                                                                                    onClick={() => setAdjustmentUnits(adjustmentUnits + 1)}
                                                                                >
                                                                                    <PlusCircle className='h-4 w-4' />
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                        <div className='space-y-2'>
                                                                            <Label>Begründung *</Label>
                                                                            <Textarea
                                                                                value={adjustmentReason}
                                                                                onChange={(e) => setAdjustmentReason(e.target.value)}
                                                                                placeholder='Grund für die Anpassung...'
                                                                                rows={2}
                                                                            />
                                                                        </div>
                                                                        <div className='flex gap-2'>
                                                                            <Button
                                                                                variant='outline'
                                                                                size='sm'
                                                                                onClick={() => {
                                                                                    setShowAdjustmentForm(false);
                                                                                    setAdjustmentUnits(0);
                                                                                    setAdjustmentReason('');
                                                                                }}
                                                                            >
                                                                                Abbrechen
                                                                            </Button>
                                                                            <Button
                                                                                size='sm'
                                                                                onClick={handleAdjust}
                                                                                disabled={adjustmentUnits === 0 || !adjustmentReason.trim() || adjustMutation.isPending}
                                                                            >
                                                                                {adjustMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                                                                Anpassen
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </div>
                        )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default BundleVouchersDialog;
